/**
 * {@link PermissionManagement} — grant and revoke score-entry permissions,
 * list users with score-entry access.
 *
 * Requirements: 4.1, 4.3
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

export interface PermissionItem {
  userId: string;
  email?: string;
  name?: string;
  isAdmin?: boolean;
}

export interface PermissionManagementProps {
  eventId: string;
  apiClient: ApiClient;
}

const sectionStyle: CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '1rem',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const listItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.5rem 0',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  borderRadius: 'var(--radius-button, 8px)',
  border: '1px solid var(--color-border, #E5E7EB)',
};

const badgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '0.15rem 0.4rem',
  borderRadius: '0.25rem',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  fontWeight: 500,
  background: '#e6f4ea',
  color: 'var(--color-brand-green, #1B4332)',
  marginLeft: '0.5rem',
};

export function PermissionManagement({
  eventId,
  apiClient,
}: PermissionManagementProps): JSX.Element {
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grantInput, setGrantInput] = useState('');
  const [granting, setGranting] = useState(false);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.request<{ permissions: PermissionItem[] }>(
        'GET',
        `/events/${encodeURIComponent(eventId)}/permissions`,
      );
      setPermissions(data.permissions);
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load permissions.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, eventId]);

  useEffect(() => {
    void fetchPermissions();
  }, [fetchPermissions]);

  async function handleGrant(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const value = grantInput.trim();
    if (!value) return;

    setGranting(true);
    setError(null);
    try {
      await apiClient.request<unknown>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/permissions`,
        { body: { email: value } },
      );
      setGrantInput('');
      await fetchPermissions();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to grant permission.');
    } finally {
      setGranting(false);
    }
  }

  async function handleRevoke(userId: string): Promise<void> {
    setError(null);
    try {
      await apiClient.request<void>(
        'DELETE',
        `/events/${encodeURIComponent(eventId)}/permissions/${encodeURIComponent(userId)}`,
      );
      await fetchPermissions();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to revoke permission.');
    }
  }

  return (
    <section style={sectionStyle} aria-labelledby="permission-mgmt-heading">
      <h3 id="permission-mgmt-heading" style={headingStyle}>
        Score Entry Permissions
      </h3>

      {error && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {/* Grant permission form */}
      <form
        onSubmit={(e) => { void handleGrant(e); }}
        style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}
        noValidate
      >
        <label htmlFor="grant-permission-input" style={{ display: 'none' }}>
          User email for score entry permission
        </label>
        <input
          id="grant-permission-input"
          type="text"
          placeholder="Grant score entry by email"
          value={grantInput}
          onChange={(e) => setGrantInput(e.target.value)}
          style={inputStyle}
          aria-label="User email for score entry permission"
        />
        <Button type="submit" disabled={granting || !grantInput.trim()}>
          {granting ? <Spinner size="1rem" label="Granting…" /> : 'Grant'}
        </Button>
      </form>

      {/* Permission list */}
      {loading ? (
        <Spinner label="Loading permissions…" />
      ) : permissions.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No score entry permissions granted.</p>
      ) : (
        <ul style={listStyle} aria-label="Score entry permission list">
          {permissions.map((perm) => (
            <li key={perm.userId} style={listItemStyle}>
              <div>
                <span style={{ fontWeight: 500 }}>
                  {perm.name || perm.email || perm.userId}
                </span>
                {perm.isAdmin && <span style={badgeStyle}>Admin</span>}
              </div>
              <Button
                variant="secondary"
                onClick={() => { void handleRevoke(perm.userId); }}
                disabled={!!perm.isAdmin}
                aria-label={`Revoke score entry permission for ${perm.name || perm.email || perm.userId}`}
                title={perm.isAdmin ? 'Admin permissions cannot be revoked' : undefined}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p style={{ color: 'var(--color-text-secondary, #6B7280)', fontSize: 'var(--font-size-body-sm, 0.875rem)', marginTop: '0.5rem' }}>
        Admins automatically have score entry permissions and cannot be revoked.
      </p>
    </section>
  );
}
